import React, { cloneElement, memo } from 'react';
import { Check, User, BookOpen, Calendar, Brain, Shield } from 'lucide-react';
export function SolutionSection() {
  return <section className="py-20 px-4 bg-gray-50" data-id="element-295">
      <div className="max-w-5xl mx-auto" data-id="element-296">
        {/* Main Solution Header */}
        <div className="text-center mb-16" data-id="element-297">
          <span className="text-orange-600 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-298">
            La solución concreta
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-blue-900 mb-6" data-id="element-299">
            Teoría de las Organizaciones 360
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto" data-id="element-300">
            Un sistema completo de enseñanza, diseñado para que puedas dar
            clases claras y ordenadas sin sacrificar tu tiempo personal.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-301">
          <BenefitCard icon={<BookOpen data-id="element-303" />} title="Dar clases claras" desc="Contenidos ordenados y listos para explicar." data-id="element-302" />
          <BenefitCard icon={<Shield data-id="element-305" />} title="Enseñar con seguridad" desc="Material validado y completo." data-id="element-304" />
          <BenefitCard icon={<ClockIcon data-id="element-307" />} title="Ahorrar horas" desc="Olvidate de planificar los fines de semana." data-id="element-306" />
          <BenefitCard icon={<Check data-id="element-309" />} title="Evaluar con coherencia" desc="Rúbricas y criterios claros." data-id="element-308" />
          <BenefitCard icon={<Brain data-id="element-311" />} title="Sin agotamiento" desc="Llegá a fin de año con energía." data-id="element-310" />
          <BenefitCard icon={<Calendar data-id="element-313" />} title="Todo el año" desc="12 módulos que cubren el ciclo completo." data-id="element-312" />
        </div>

        {/* The "Not just theory" block */}
        <div className="bg-blue-900 text-white rounded-2xl p-8 md:p-12 text-center shadow-xl mb-20" data-id="element-314">
          <p className="text-lg md:text-xl opacity-90 mb-6" data-id="element-315">
            No es teoría suelta. No es un manual abstracto. No es "material para
            adaptar algún día".
          </p>
          <h3 className="text-2xl md:text-3xl font-bold text-orange-400 mb-8" data-id="element-316">
            👉 Es un recorrido anual completo, listo para usar desde el primer
            día de clases.
          </h3>
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 text-lg font-medium" data-id="element-317">
            <div className="flex items-center gap-2" data-id="element-318">
              <span className="bg-white text-blue-900 w-8 h-8 rounded-full flex items-center justify-center font-bold" data-id="element-319">
                1
              </span>
              Abrís el archivo
            </div>
            <div className="hidden md:block text-orange-400" data-id="element-320">→</div>
            <div className="flex items-center gap-2" data-id="element-321">
              <span className="bg-white text-blue-900 w-8 h-8 rounded-full flex items-center justify-center font-bold" data-id="element-322">
                2
              </span>
              Das la clase
            </div>
            <div className="hidden md:block text-orange-400" data-id="element-323">→</div>
            <div className="flex items-center gap-2" data-id="element-324">
              <span className="bg-white text-blue-900 w-8 h-8 rounded-full flex items-center justify-center font-bold" data-id="element-325">
                3
              </span>
              El año avanza
            </div>
          </div>
        </div>

        {/* Target Audience */}
        <div className="grid md:grid-cols-2 gap-12" data-id="element-326">
          <div data-id="element-327">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-orange-500 inline-block pb-2" data-id="element-328">
              ¿Para quién es este material?
            </h3>
            <ul className="space-y-4" data-id="element-329">
              <AudienceItem text="Dictás Teoría de las Organizaciones en secundaria" data-id="element-330" />
              <AudienceItem text="Tenés cursos reales, alumnos reales y tiempos reales" data-id="element-331" />
              <AudienceItem text="Querés enseñar bien, pero no querés vivir armando material" data-id="element-332" />
              <AudienceItem text="Te importa que los estudiantes entiendan, no que memoricen" data-id="element-333" />
              <AudienceItem text="Estás cansado de improvisar clases y evaluaciones" data-id="element-334" />
            </ul>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100" data-id="element-335">
            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-336">
              Y también es para vos si pensaste:
            </h3>
            <div className="space-y-4" data-id="element-337">
              <QuoteBubble text="La materia es importante, pero así como está planteada no engancha" data-id="element-338" />
              <QuoteBubble text="Podría dar mejores clases si tuviera mejores recursos" data-id="element-339" />
              <QuoteBubble text="No quiero pasar otro año sobreviviendo semana a semana" data-id="element-340" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function BenefitCard({
  icon,
  title,
  desc
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-341">
      <div className="text-blue-600 mb-4 h-8 w-8" data-id="element-342">
        {cloneElement(icon as React.ReactElement, {
        size: 32
      })}
      </div>
      <h4 className="font-bold text-gray-900 mb-2" data-id="element-343">{title}</h4>
      <p className="text-sm text-gray-600" data-id="element-344">{desc}</p>
    </div>;
}
function AudienceItem({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-3" data-id="element-345">
      <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" data-id="element-346" />
      <span className="text-gray-700" data-id="element-347">{text}</span>
    </li>;
}
function QuoteBubble({
  text
}: {
  text: string;
}) {
  return <div className="bg-gray-50 p-4 rounded-lg rounded-tl-none border border-gray-200 italic text-gray-600 text-sm" data-id="element-348">
      "{text}"
    </div>;
}
function ClockIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-349">
      <circle cx="12" cy="12" r="10" data-id="element-350"></circle>
      <polyline points="12 6 12 12 16 14" data-id="element-351"></polyline>
    </svg>;
}